import { TabItem } from './../../shared/bb-page-header/bb-page-header.component';
import { ActivatedRoute, Params, RouterStateSnapshot } from '@angular/router';
import { Subscription, Observable, Subject } from 'rxjs';
import { PrebidService } from 'app/services/prebid.service';
import { TranslateService } from '@ngx-translate/core';
import { SapiService } from './../../../services/sapi.service';
import { OverlayService } from './../../../services/overlay.service';
import { Component, OnInit, ViewChild, OnDestroy, Input } from '@angular/core';
import { NgForm } from '@angular/forms';
import { removeNullishFromObjRecursive } from 'app/shared/utils/removeNullishFromObjRecursive.util';

@Component({
	selector: 'bid-adapters',
	templateUrl: './bid-adapters.component.html',
	styleUrls: ['./bid-adapters.component.scss']
})

export class BidAdaptersComponent implements OnInit, OnDestroy {
	@Input() mode: string;
	@ViewChild('formRef', { static: true }) form: NgForm;

	publicationPrebidParams: any;
	activeTab: string;
	subscriptions = new Subscription();
	tabItems: TabItem[];

	constructor(
		private sapiService: SapiService,
		private overlayService: OverlayService,
		private prebidService: PrebidService,
		private translate: TranslateService,
		public route: ActivatedRoute
	) {
	}

	ngOnInit() {
		this.tabItems = [
			{
				title: this.translate.instant('AUCTION'),
				routerLink: ['/publication/global-prebid-settings', 'auction'],
				icon: 'tags',
			},
			{
				title: this.translate.instant('MODULES'),
				routerLink: ['/publication/global-prebid-settings', 'modules'],
				icon: 'cubes',
			},
			{
				title: this.translate.instant('ADAPTERS'),
				routerLink: ['/publication/global-prebid-settings', 'adapters'],
				icon: 'plug',
			},
		];

		this.getPrebidParams(true);
		this.subscriptions.add(this.route.params.subscribe((params: Params) => {
			this.activeTab = params.tab;
		}));
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}

	save() {
		this.overlayService.setLoading(true);
		removeNullishFromObjRecursive(this.publicationPrebidParams);
		this.sapiService.putUrl('/publication/prebidParams', this.publicationPrebidParams).subscribe(() => {
			this.prebidService.publicationPrebidParams = this.publicationPrebidParams;
			this.overlayService.setLoading(false);
			this.overlayService.createToast(this.translate.instant('ENTITY_SAVED', {
				entity: this.translate.instant('PREBID_CONFIGURATION'),
				id: ''
			}), 'save');
			this.form.form.markAsPristine();
		});
	}

	cancel() {
		this.getPrebidParams(true);
		this.form.form.markAsPristine();
	}

	getPrebidParams(forceReload = false) {
		if (!forceReload && this.prebidService.publicationPrebidParams) {
			this.handlePublicationPrebidParams(this.prebidService.publicationPrebidParams);
		} else {
			this.overlayService.setLoading(true);
			this.sapiService.getUrl('/publication/prebidParams').subscribe((publicationPrebidParams) => {
				this.overlayService.setLoading(false);
				this.handlePublicationPrebidParams(publicationPrebidParams);
			}, (err) => {
				this.overlayService.setLoading(false);
				console.warn('Error getting prebidParams from publication.', err);
				if (err.status === 404) {
					this.publicationPrebidParams = err.status === 404 ? {
						__config__: {}
					} : null;
				}
			});
		}
	}

	handlePublicationPrebidParams(publicationPrebidParams) {
		this.publicationPrebidParams = publicationPrebidParams;
	}

	prebidParamsChanged() {
		this.form.form.markAsDirty();
	}

	allowNavigateAway(currentState: RouterStateSnapshot, nextState: RouterStateSnapshot): Observable<boolean> | boolean {
		// allow if not dirty
		if (this.form.form && !this.form.form.dirty) {
			return true;
		}

		// allow if navigating to sibling route
		const currentParent = currentState.url.split('/').slice(0, -1).join('/');
		const nextParent = nextState.url.split('/').slice(0, -1).join('/');

		if (currentParent === nextParent) {
			return true;
		}

		// ask user
		const allowNavigationObservable = new Subject<boolean>();

		const dialog = {
			icon: 'warning',
			title: this.translate.instant('PROMPT_SURE_TO_LEAVE_APPLICATION'),
			contents: this.translate.instant('PROMPT_SURE_TO_LEAVE_PAGE'),
			showSave: true,
			onConfirm: () => {
				allowNavigationObservable.next(true);
			},
			onCancel: () => {
				allowNavigationObservable.next(false);
			},
			onSaveAndClose: () => {
				this.save();
				allowNavigationObservable.next(true);
			}
		};

		this.overlayService.createDialog(dialog);

		return allowNavigationObservable;
	}
}
